import React, { useState, useEffect, useCallback } from 'react'
import { Button, Banner, EmptyState } from '@ui'
import { OrgNode } from './OrgNode.jsx'
import './OrgTree.css'

function collectDescendantIds(node, ids) {
  if (!node.reports) return
  for (const child of node.reports) {
    ids.add(child.userId)
    collectDescendantIds(child, ids)
  }
}

function collectAllIds(nodes, ids) {
  for (const node of nodes) {
    ids.add(node.userId)
    collectDescendantIds(node, ids)
  }
}

export function OrgTree({ tree, orphans, loading, error, onRetry, onSelect, onAssignManager }) {
  const [expandedIds, setExpandedIds] = useState(() => new Set())

  // Default-expand root nodes when tree loads
  useEffect(() => {
    if (tree && tree.length > 0) {
      setExpandedIds(new Set(tree.map((n) => n.userId)))
    }
  }, [tree])

  const toggle = useCallback((nodeId, forceState) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      const shouldExpand = forceState !== undefined ? forceState : !next.has(nodeId)
      if (shouldExpand) {
        next.add(nodeId)
      } else {
        next.delete(nodeId)
        // Collapse all descendants too
        const findNode = (nodes) => {
          for (const n of nodes) {
            if (n.userId === nodeId) return n
            if (n.reports) {
              const found = findNode(n.reports)
              if (found) return found
            }
          }
          return null
        }
        const node = findNode(tree)
        if (node) {
          const descendantIds = new Set()
          collectDescendantIds(node, descendantIds)
          for (const id of descendantIds) next.delete(id)
        }
      }
      return next
    })
  }, [tree])

  const expandAll = useCallback(() => {
    const ids = new Set()
    collectAllIds(tree, ids)
    setExpandedIds(ids)
  }, [tree])

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set())
  }, [])

  if (loading) {
    return (
      <div className="org-tree__skeleton">
        <div className="org-tree__skeleton-card" />
        <div className="org-tree__skeleton-card" />
        <div className="org-tree__skeleton-card" />
      </div>
    )
  }

  if (error) {
    return (
      <Banner tone="error">
        Couldn&rsquo;t load org structure &mdash; try again.{' '}
        <Button variant="ghost" size="sm" onClick={onRetry}>Retry</Button>
      </Banner>
    )
  }

  if ((!tree || tree.length === 0) && (!orphans || orphans.length === 0)) {
    return <EmptyState title="No team members yet." />
  }

  return (
    <div className="org-tree">
      <div className="org-tree__toolbar">
        <Button variant="ghost" size="sm" onClick={expandAll}>Expand all</Button>
        <Button variant="ghost" size="sm" onClick={collapseAll}>Collapse all</Button>
      </div>

      <ul role="tree" aria-label="Organisation structure" className="org-tree__list">
        {tree.map((root) => (
          <OrgNode
            key={root.userId}
            node={root}
            depth={0}
            expandedIds={expandedIds}
            onToggle={toggle}
            onSelect={onSelect}
          />
        ))}
      </ul>

      {orphans && orphans.length > 0 && (
        <div className="org-tree__orphans">
          <div className="org-tree__orphans-title">Orphaned users — no manager assigned</div>
          <div className="org-tree__orphan-list">
            {orphans.map((u) => (
              <div key={u.userId} className="org-tree__orphan-item">
                <span className="org-tree__orphan-name">{u.name || u.email}</span>
                {onAssignManager && (
                  <Button size="sm" onClick={() => onAssignManager(u)}>
                    Assign manager
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
